import PropTypes from 'prop-types';
import { CANDY_APPLE } from 'HubStyleTokens/colors';
import UIAlert from 'UIComponents/alert/UIAlert';
import UILink from 'UIComponents/link/UILink';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import PortalIdParser from 'PortalIdParser';
import { DOMAIN_VERIFICATION_STATUS } from '../data/models/ApplicationAuthorization';
import { useSelector } from 'react-redux';
import { getIsRequestingNewScopes } from '../selectors/AppSelectors';
import { getAccountName } from '../selectors/AccountSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const portalId = PortalIdParser.get();
const AuthorizeAlert = ({
  domainStatus,
  cannotInstall
}) => {
  const hasVerifiedDomain = domainStatus === DOMAIN_VERIFICATION_STATUS.VERIFIED_DOMAIN;
  const isRequestingNewScopes = useSelector(getIsRequestingNewScopes);
  const accountName = useSelector(getAccountName);
  const translationKey = hasVerifiedDomain ? 'verified' : 'notVerified';
  const previouslyConnectedButNoInstall = !isRequestingNewScopes && cannotInstall;

  // We have three cases here, sorted in order of priority in terms of the alert overrides:
  // 1.) Has the app been previously installed but the user does not have app marketplace access
  // 2.) If the app hasn't been previously installed, does the user not have app marketplace access?
  // 3.) If the app has a verified domain or not
  const renderAlertTitle = () => {
    if (previouslyConnectedButNoInstall) {
      if (accountName) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.app.marketplaceAccess.alreadyConnectedTitle",
          options: {
            accountName
          }
        });
      }
      // If no account name has been specified
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.alreadyConnectedTitlePortal",
        options: {
          portalId
        }
      });
    }
    if (cannotInstall) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.needPermissionTitle"
      });
    }
    if (hasVerifiedDomain) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.notListed.verifiedTitle"
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "warning",
        size: "xs",
        color: CANDY_APPLE,
        className: "p-right-2"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.notListed.notVerifiedTitle"
      })]
    });
  };
  const renderAlertBody = () => {
    if (previouslyConnectedButNoInstall) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.alreadyConnectedBody"
      });
    }
    if (cannotInstall) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "oauth.app.marketplaceAccess.needPermissionBody_jsx",
        elements: {
          Link: UILink
        },
        options: {
          href: 'https://knowledge.hubspot.com/settings/hubspot-user-permissions-guide#settings'
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `oauth.app.notListed.${translationKey}Body`
    });
  };
  const getAlertType = () => {
    if (hasVerifiedDomain && !cannotInstall) {
      return 'warning';
    } else if (previouslyConnectedButNoInstall) {
      return 'info';
    }
    return 'danger';
  };
  return /*#__PURE__*/_jsx(UIAlert, {
    titleText: renderAlertTitle(),
    className: "m-top-4",
    type: getAlertType(),
    children: renderAlertBody()
  });
};
AuthorizeAlert.propTypes = {
  domainStatus: PropTypes.string.isRequired,
  cannotInstall: PropTypes.bool.isRequired
};
export default AuthorizeAlert;